import type { ReactNode } from 'react'

import { ApiError, type Problem } from '../../lib/problem'
import { useOnline } from '../../lib/useOnline'
import type { ArmLanding } from '../../lib/useRetryLanding'
import { Button } from './Button'
import { Notice } from './Notice'

/**
 * A failed request, rendered the way §7.1 asks for it.
 *
 * > *"Loading, empty, error and offline states for every screen. A backend
 * > Problem renders its message and code; a network failure renders a retry."*
 *
 * Until Sprint 1.9 Stage 7b **twelve of the fifteen surfaces that render a
 * Problem dropped its `code`**, and three dropped the message too — Today,
 * Baseline and the detail view each showed a sentence this client wrote and
 * nothing the server said. The code was never unavailable: `ApiError.code`
 * exists, is tested (`api.test.ts:108`), and every one of those screens was
 * holding it. §7.1 is the only clause in §7 the sprint's own test plan
 * (§8.1–8.7) never names, which is how no stage came to own it.
 *
 * ── THE SHAPE, AND WHY IT IS NOT JUST `problem.detail` ──────────────────────
 *
 * Three lines, in this order:
 *
 *   1. **The screen's own sentence.** Backend `detail` is written for whoever
 *      is reading a log, and this is a consumer product whose Context section
 *      asks for non-diagnostic framing. "Your baseline could not be loaded" is
 *      the screen's job and stays the screen's job.
 *   2. **The server's `detail`**, verbatim, when there is one. This is the half
 *      that was missing and it is what makes the notice true rather than
 *      merely reassuring.
 *   3. **`Reference: <code>`.** The machine-readable half — the thing a person
 *      can quote, and the thing that distinguishes two failures that read
 *      identically. `Record.tsx` already did exactly this for a terminal job,
 *      250 lines from a `messageFor()` that discarded the code; this is that
 *      pattern, shared.
 *
 * A failure that is NOT an `ApiError` never reached the server at all, so there
 * is no Problem and no code to show. That case gets the offline sentence and
 * the retry, which is the second half of the clause.
 *
 * `onRetry` is optional only because a few callers genuinely have nothing to
 * retry. Four admin screens had no retry at all and a browser reload was the
 * only recovery — one that discards the filters they were holding.
 */
export function ProblemNotice({
  children,
  error,
  onRetry,
  retryLabel = 'Try again',
  retryBusy = false,
  landing,
}: {
  /** The screen's own sentence. Written for the person, not for the log. */
  children: ReactNode
  error: unknown
  onRetry?: () => void
  retryLabel?: string
  /**
   * In flight. DISABLES the retry rather than removing it, which is trap 37:
   * React flushes a discrete event synchronously, so a handler's own
   * re-entrancy guard is unreachable and the `disabled` attribute is the only
   * thing that actually holds. Hiding the control instead breaks the guard the
   * journal's double-click test asserts -- measured, by doing exactly that.
   */
  retryBusy?: boolean
  /**
   * From `useRetryLanding`, on screens whose notice unmounts when the error
   * clears -- which is all of them that pass an `onRetry`. Optional so a notice
   * with no retry, or one whose host has not been wired yet, stays valid.
   *
   * The ARMING half only: this component records whether its button held focus
   * and never moves focus itself. The firing has to run from the screen, for
   * the layout-effect ordering reason in `useRetryLanding`'s header.
   */
  landing?: ArmLanding
}) {
  /**
   * TWO QUESTIONS, NOT ONE, and conflating them told users a falsehood.
   *
   * `reachedServer` is `error instanceof ApiError` — the request got an HTTP
   * response. `problem` is whether that response carried a parseable Problem
   * body, and it can be null on a response that certainly arrived:
   * `UnauthorizedError` and `ForbiddenError` both take `problem: Problem | null`
   * and `api.ts` constructs them with null whenever the body will not parse —
   * an empty body, HTML from a proxy, a gateway 403.
   *
   * The first version branched on `problem === null` alone, so an ALB or WAF
   * 403 with an HTML body rendered "The request did not reach the server. Check
   * your connection and try again." — blaming the user's connection for a
   * refusal, and inviting a retry that cannot succeed.
   *
   * The middle sentence is the one `Record.tsx`'s deleted `messageFor()` had
   * for exactly this case, and losing it is how the falsehood got in.
   */
  const reachedServer = error instanceof ApiError
  const problem: Problem | null = reachedServer ? error.problem : null
  const online = useOnline()

  return (
    <Notice tone="error" className="notice--problem">
      <p>{children}</p>
      {problem === null ? (
        <p className="problem__detail">
          {/**
            * THREE ARMS, AND THE ORDER IS THE WHOLE OF THE CORRECTNESS.
            *
            * `reachedServer` stays on top and is untouched: a response ARRIVING
            * is proof of connectivity, whatever `navigator.onLine` claims. That
            * is what stops this re-creating the Stage 7b defect from the other
            * direction -- a WAF 403 arriving while the browser reports itself
            * offline still says the server refused it.
            *
            * `navigator.onLine` is consulted only INSIDE the branch that has
            * already established no response arrived, because `false` is
            * reliable and `true` is not (`useOnline`'s header). The third arm
            * is what this said for every non-`ApiError` rejection before --
            * including a 15s `AbortError` from `fetchWithTimeout`
            * (`api.ts:75`), which told a user with a working connection to
            * check their connection. §7.1 names "offline states"; that is the
            * clause, and the sentence below is the honest half of it.
            */}
          {reachedServer
            ? 'The server refused that, and did not say why.'
            : online
              ? 'The request did not reach the server. Try again in a moment.'
              : 'You are offline, so that never left this device.'}
        </p>
      ) : (
        <>
          {/* `detail || title`, which is what BOTH replaced implementations
              did -- `ProblemError`'s constructor and `Record.tsx`'s
              `messageFor()`. A Problem with an empty detail would otherwise
              show the screen's sentence and a bare code where it used to show
              the title. */}
          {problem.detail || problem.title ? (
            <p className="problem__detail">{problem.detail || problem.title}</p>
          ) : null}
          {/* Guarded like the line above it. The type says `string`, but the
              runtime gate is `isProblem`, which accepts `""` -- and an empty
              one rendered the label alone: "Reference: ". */}
          {problem.code ? (
            <p className="problem__ref">
              Reference: <code className="mono-cell">{problem.code}</code>
            </p>
          ) : null}
        </>
      )}
      {onRetry && (
        <Button
          variant="ghost"
          size="sm"
          busy={retryBusy}
          onClick={(event) => {
            /**
             * Recorded, not assumed. On WebKit a click does not focus a button
             * (trap 33), so a tap and a keyboard activation leave
             * `document.activeElement` in different places and only one of them
             * is owed a landing when this notice unmounts. `LoadMore` captures
             * the same fact in its own handler (`DataTable.tsx:212`).
             *
             * `landing` is typed as the arming half alone, so this component is
             * structurally incapable of moving focus itself -- which it must
             * not do, for the reason `useRetryLanding`'s header gives.
             */
            landing?.(document.activeElement === event.currentTarget)
            onRetry()
          }}
        >
          {retryLabel}
        </Button>
      )}
    </Notice>
  )
}
